import { useCallback, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SIGNATURE_MILESTONES } from "@/lib/milestoneCopy";

/**
 * Full-viewport modal celebration. Floats above all page content with a kraft
 * backdrop so it cannot be hidden behind the project cover photo, the browser
 * chrome, or whatever step the user happens to be scrolled to. Dismiss with
 * the "Keep going" button — which persists `milestone-shown` so it never
 * re-fires for that key.
 *
 * Signature milestones (p50, p100) use a stronger, more celebratory treatment.
 *
 * Not a toast: it sits centred on the screen with a backdrop. This is
 * intentional — the user told us mid-test it was easy to miss the previous
 * inline banner.
 */
export default function MilestoneCelebrationSheet({ projectId, milestone, onDismissed }) {
  const mKey = milestone?.key;

  const handleDismiss = useCallback(async () => {
    if (!projectId || !mKey) return;
    try {
      await api.post(`/projects/${projectId}/milestone-shown`, { key: mKey });
    } catch {
      // Best-effort. Local dismissal still happens.
    }
    onDismissed?.();
  }, [projectId, mKey, onDismissed]);

  // Esc dismisses on desktop / external keyboard.
  useEffect(() => {
    if (!mKey) return undefined;
    const onKey = (e) => { if (e.key === "Escape") handleDismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mKey, handleDismiss]);

  if (!milestone) return null;

  const isSignature = SIGNATURE_MILESTONES.has(milestone.key);
  const isComplete = milestone.key === "p100";

  return (
    <div
      data-testid={`milestone-celebration-${milestone.key}`}
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      className="fixed inset-0 z-[120] flex items-center justify-center px-5"
      style={{
        background: "rgba(58, 46, 42, 0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
      onClick={handleDismiss}
    >
      <div
        // Stop click bubbling so tapping the card doesn't auto-dismiss.
        onClick={(e) => e.stopPropagation()}
        className={[
          "relative w-full max-w-sm rounded-2xl border-2 shadow-2xl overflow-hidden",
          isSignature
            ? "bg-masking border-timber-900 shadow-tactile"
            : "bg-chalk border-stone_border",
        ].join(" ")}
      >
        {/* Top accent strip — visual cue this is a celebration not an error */}
        <div
          className={[
            "h-2 w-full",
            isComplete ? "bg-ok" : isSignature ? "bg-timber-900" : "bg-masking",
          ].join(" ")}
        />
        <div className="p-6 text-center">
          {/* Big hammer mark in a celebratory frame */}
          <div className="mx-auto w-16 h-16 rounded-2xl bg-timber-900 flex items-center justify-center shadow-tactile">
            <img src="/logo.svg" alt="" className="w-12 h-12" draggable={false} />
          </div>
          <p
            data-testid={`milestone-celebration-${milestone.key}-headline`}
            className={[
              "font-heading font-bold mt-4 leading-tight",
              isSignature ? "text-3xl text-timber-900" : "text-2xl text-timber-900",
            ].join(" ")}
          >
            {milestone.headline}
          </p>
          <p
            className={[
              "mt-3 leading-relaxed whitespace-pre-line",
              isSignature ? "text-base text-timber-900/90" : "text-sm text-timber-700",
            ].join(" ")}
          >
            {milestone.sub}
          </p>
          <Button
            data-testid={`milestone-celebration-${milestone.key}-dismiss`}
            onClick={handleDismiss}
            className={[
              "mt-6 min-h-[52px] px-7 text-base font-bold btn-press",
              isComplete
                ? "bg-ok text-white hover:bg-ok/90"
                : isSignature
                ? "bg-timber-900 text-chalk hover:bg-timber-700"
                : "bg-masking text-timber-900 hover:bg-masking-600 hover:text-white",
            ].join(" ")}
          >
            {isComplete ? "Yep. I did that." : "Keep going"}
          </Button>
        </div>
      </div>
    </div>
  );
}
